<script setup lang="ts">
import { computed, onMounted, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskCopyright> in <template>
import KioskCopyright from '@/app/layouts/kiosk/KioskCopyright.vue';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskLayout> in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useDeveloperStore } from '@/app/stores/developer';
import { useUiStore } from '@/app/stores/ui';
import type { AttractAssignment } from '@/services/attract/experiment';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
// biome-ignore lint/correctness/noUnusedImports: used via tag <HomeShowcase> in <template>
import HomeShowcase from './home/HomeShowcase.vue';
// biome-ignore lint/correctness/noUnusedImports: used via tag <HomeSplashscreen> in <template>
import HomeSplashscreen from './home/HomeSplashscreen.vue';
import { useAttractLoop } from './home/useAttractLoop';
import { useHomeSessionStart } from './home/useHomeSessionStart';
import {
  type HomeSplashVisibility,
  SPLASH_SCREEN_DELAY,
  useHomeSplashscreen,
} from './home/useHomeSplashscreen';

interface HomePublicApi {
  readonly SPLASH_SCREEN_DELAY: number;
  readonly attractAssignment: AttractAssignment | null;
  readonly developer: ReturnType<typeof useDeveloperStore>;
  readonly isCreatingSession: boolean;
  readonly isSplashScreenDelayExpired: boolean;
  readonly isSplashScreenLoaded: boolean;
  readonly mountedAt: number | null;
  readonly router: typeof router;
  readonly showSplashScreen: HomeSplashVisibility;
  readonly splashscreenBackgroundUrl: string | null;
  readonly splashScreenVideoUrl: string | null | undefined;
  readonly store: typeof store;
  readonly ui: ReturnType<typeof useUiStore>;
  onSplashScreenClick: () => void;
  onSplashScreenVideoLoaded: () => void;
  onStartButtonClick: () => Promise<void>;
}

const developer = useDeveloperStore();
const ui = useUiStore();

const { t, te } = useI18n();

const mountedAt = shallowRef<number | null>(null);
const splashscreenBackgroundUrl = computed<string | null>(
  () => store.kiosk?.splashscreen_background_url || null
);
const {
  assignment: attractAssignment,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  preloadVideos: attractPreloadVideos,
} = useAttractLoop({ isBusy: () => isCreatingSession.value });
const splashScreenVideoUrl = computed<string | null | undefined>(() =>
  attractAssignment.value
    ? attractAssignment.value.videoUrl
    : store.kiosk?.splashscreen_video_url
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const isSplashScreenVideoEnabled = computed(
  () => !store.viewportIsPortrait || store.isVerticalKiosk
);
const isSplashscreenHidden = computed(() => developer.hideSplashscreen);

const {
  isSplashScreenDelayExpired,
  isSplashScreenLoaded,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  onPreloadVideoFailed,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  onSplashScreenVideoFailed,
  onSplashScreenVideoLoaded,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  onSplashScreenVideoStarted,
  showSplashScreen,
  trackSplashScreenDismiss,
} = useHomeSplashscreen({
  isHidden: isSplashscreenHidden,
  mountedAt,
  videoUrl: splashScreenVideoUrl,
});

const { isCreatingSession, onStartButtonClick } = useHomeSessionStart({
  mountedAt,
  showSplashScreen,
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const startLabel = computed(() => {
  if (!isCreatingSession.value) {
    return t('Home.button');
  }
  return te('Common.loading') ? t('Common.loading') : 'Loading...';
});

function onSplashScreenClick(): void {
  if (!trackSplashScreenDismiss()) {
    return;
  }
  void onStartButtonClick();
}

const publicApi: HomePublicApi = {
  SPLASH_SCREEN_DELAY,
  get attractAssignment(): AttractAssignment | null {
    return attractAssignment.value;
  },
  developer,
  get isCreatingSession(): boolean {
    return isCreatingSession.value;
  },
  get isSplashScreenDelayExpired(): boolean {
    return isSplashScreenDelayExpired.value;
  },
  get isSplashScreenLoaded(): boolean {
    return isSplashScreenLoaded.value;
  },
  get mountedAt(): number | null {
    return mountedAt.value;
  },
  router,
  get showSplashScreen(): HomeSplashVisibility {
    return showSplashScreen.value;
  },
  get splashscreenBackgroundUrl(): string | null {
    return splashscreenBackgroundUrl.value;
  },
  get splashScreenVideoUrl(): string | null | undefined {
    return splashScreenVideoUrl.value;
  },
  store,
  ui,
  onSplashScreenClick,
  onSplashScreenVideoLoaded,
  onStartButtonClick,
};

onMounted(() => {
  exposeToGlobalScope(publicApi, 'Home');
});

defineExpose(publicApi);
</script>

<template>
  <KioskLayout :is-centered="true">
    <template #KioskLayout__bottom>
      <KioskCopyright/>
    </template>

    <HomeSplashscreen
      :background-url="splashscreenBackgroundUrl"
      :is-video-enabled="isSplashScreenVideoEnabled"
      :preload-videos="attractPreloadVideos"
      :show-video="showSplashScreen"
      :video-url="splashScreenVideoUrl"
      @dismiss="onSplashScreenClick"
      @preload-failed="onPreloadVideoFailed"
      @video-failed="onSplashScreenVideoFailed"
      @video-loaded="onSplashScreenVideoLoaded"
      @video-started="onSplashScreenVideoStarted"
    />

    <HomeShowcase
      :is-creating-session="isCreatingSession"
      :sample-photo-url="store.kiosk?.sample_photo_url"
      :sample-portrait-url="store.kiosk?.sample_portrait_url"
      :start-label="startLabel"
      :title="t('Home.title')"
      @start="onStartButtonClick"
    />
  </KioskLayout>
</template>
